/**
 * useAutoSave Hook
 * Provides debounced auto-save functionality for Import Wizard session state
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { updateImportSession } from '../supabase-client'
import { saveSessionState } from '../utils/sessionStateManager'

/**
 * Auto-saves import session state to database with debouncing
 *
 * @param {string} sessionId - Import session ID
 * @param {Object} state - Current state to save
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 3000)
 * @param {boolean} options.enabled - Whether auto-save is enabled (default: true)
 * @returns {Object} { saveStatus: string, forceSave: function, lastSaved: Date }
 */
export const useAutoSave = (sessionId, state, options = {}) => {
  const { debounceMs = 3000, enabled = true } = options

  const timeoutRef = useRef(null)
  const [saveStatus, setSaveStatus] = useState('idle') // idle, saving, saved, error
  const [lastSaved, setLastSaved] = useState(null)
  const [error, setError] = useState(null)

  // Save function (respects enabled flag)
  const save = useCallback(async (force = false) => {
    if (!sessionId) {
      console.log('[useAutoSave] Skipping save: no sessionId')
      return
    }

    if (!enabled && !force) {
      console.log('[useAutoSave] Skipping save: auto-save disabled')
      return
    }

    console.log('[useAutoSave] Starting save...', force ? '(forced)' : '')
    setSaveStatus('saving')
    setError(null)

    try {
      // Serialize state
      const stateToSave = saveSessionState(state)

      // Update session in database
      await updateImportSession(sessionId, {
        session_state: stateToSave,
        updated_at: new Date().toISOString()
      })

      setSaveStatus('saved')
      setLastSaved(new Date())
      console.log('[useAutoSave] Save successful')

      // Auto-hide "saved" status after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle')
      }, 2000)

    } catch (err) {
      console.error('[useAutoSave] Save failed:', err)
      setSaveStatus('error')
      setError(err.message || 'Failed to save session')

      // Auto-hide error after 5 seconds
      setTimeout(() => {
        setSaveStatus('idle')
        setError(null)
      }, 5000)
    }
  }, [sessionId, state, enabled])

  // Force immediate save (bypasses enabled check)
  const forceSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    return save(true) // Pass force=true to bypass enabled check
  }, [save])

  // Debounced auto-save effect
  useEffect(() => {
    if (!enabled || !sessionId) {
      return
    }

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      save()
    }, debounceMs)

    // Cleanup on unmount or dependency change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [state, save, debounceMs, enabled, sessionId])

  return {
    saveStatus, // 'idle' | 'saving' | 'saved' | 'error'
    forceSave,  // Force immediate save
    lastSaved,  // Last successful save timestamp
    error       // Error message if save failed
  }
}
